"use client";
import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { NEXT_PUBLIC_GOOGLE_CLIENT_ID, GOOGLE_DRIVE_SCOPES } from "@/env";

// FedCM-ready prompt notification type
type PromptMomentNotification = {
    getMomentType(): 'display' | 'skipped' | 'dismissed';
};

// Add a new 'ready' flag to indicate when Google Sign-In is fully initialized
// Use a ref to always have the latest credential callback

type SDKState = {
    ready: boolean; // true only after initialize has run
    gsiReady: boolean;
    pickerReady: boolean;
    driveToken: string | null;
    requestDriveToken: (forceConsent?: boolean) => Promise<string>;
    init: (cb: (response: any) => void) => void;
    prompt: (onNotDisplayed?: () => void) => void;
    renderGoogleButton: (container: HTMLElement, cfg?: any) => void;
    signOut: () => void;
    setCodeClientCallback: (cb: (code: string) => void) => void;
    requestCode: () => void;
    requestDirectAuth: (scopes?: string) => void;
};

const GoogleSDKContext = createContext<SDKState>({
    ready: false,
    gsiReady: false,
    pickerReady: false,
    driveToken: null,
    requestDriveToken: async () => "",
    init: () => { },
    prompt: () => { },
    renderGoogleButton: () => { },
    signOut: () => { },
    setCodeClientCallback: () => { },
    requestCode: () => { },
    requestDirectAuth: () => { },
});

export const GoogleSDKProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [gsiReady, setGsiReady] = useState(false);
    const [pickerReady, setPickerReady] = useState(false);
    const [driveToken, setDriveToken] = useState<string | null>(null);
    const [ready, setReady] = useState(false); // true after initialize
    const latestCallback = useRef<(response: any) => void>(() => { });
    const [isIdInitialised, setIsIdInitialised] = useState(false);
    const [codeClient, setCodeClient] = useState<any>(null);
    const codeClientRef = useRef<any>(null); // Add a ref to track codeClient
    const codeCallbackRef = useRef<(code: string) => void>();
    const tokenRef = useRef<string | null>(null); // Store credential token for retries
    const { toast } = useToast();

    const SCOPES = GOOGLE_DRIVE_SCOPES;      // one source of truth


    // Load GSI and Picker scripts
    useEffect(() => {
        console.log('[GoogleSDKProvider] useEffect: Mounting, loading GSI and Picker scripts');
        // Load GSI
        const gsi = document.createElement("script");
        gsi.src = "https://accounts.google.com/gsi/client";
        gsi.async = true;
        gsi.defer = true;
        gsi.onload = () => {
            console.log('[GoogleSDKProvider] GSI script loaded');
            setGsiReady(true);
        };
        gsi.onerror = () => console.error("[GoogleSDKProvider] Failed loading GSI");
        document.body.appendChild(gsi);

        // Load picker API
        const picker = document.createElement("script");
        picker.src = "https://apis.google.com/js/api.js";
        picker.onload = () => {
            console.log('[GoogleSDKProvider] Picker API script loaded');
            (window as any).gapi.load("picker", () => {
                console.log('[GoogleSDKProvider] Picker loaded via gapi');
                setPickerReady(true);
            });
        };
        picker.onerror = () => console.error("[GoogleSDKProvider] Failed loading Google API");
        document.body.appendChild(picker);

        // Try to load existing token
        const storedTokenData = localStorage.getItem("google_drive_token");
        if (storedTokenData) {
            try {
                const tokenData = JSON.parse(storedTokenData);
                console.log('[GoogleSDKProvider] Found stored Google Drive token', tokenData);
                if (tokenData.expiry > Date.now()) {
                    setDriveToken(tokenData.access_token);
                } else {
                    console.log('[GoogleSDKProvider] Stored token expired, removing');
                    localStorage.removeItem("google_drive_token");
                }
            } catch (e) {
                console.error("[GoogleSDKProvider] Error parsing stored Google token:", e);
                localStorage.removeItem("google_drive_token");
            }
        } else {
            console.log('[GoogleSDKProvider] No stored Google Drive token found');
        }
    }, []);

    // Minimal, robust Google Sign-In API
    const init = useCallback((cb: (response: any) => void) => {
        console.log('[GoogleSDKProvider] init called', { gsiReady, hasGoogle: !!window.google?.accounts?.id });
        if (!gsiReady || !window.google?.accounts?.id) {
            console.warn('[GoogleSDKProvider] GSI not ready or google.accounts.id missing');
            return;
        }
        latestCallback.current = cb;
        window.google.accounts.id.initialize({
            client_id: NEXT_PUBLIC_GOOGLE_CLIENT_ID,
            callback: (res: any) => {
                console.log('[GoogleSDKProvider] Google ID callback fired', res);
                tokenRef.current = res.credential || null; // Store credential for retries
                latestCallback.current(res);
            },
            ux_mode: "popup",
            cancel_on_tap_outside: false,
            use_fedcm_for_prompt: true, // ✅ FedCM-ready
        });
        setReady(true);
        console.log('[GoogleSDKProvider] Google ID initialized');
    }, [gsiReady]);

    const prompt = useCallback((onNotDisplayed?: () => void) => {
        console.log('[GoogleSDKProvider] prompt called', { ready, hasGoogle: !!window.google?.accounts?.id });
        if (!ready || !window.google?.accounts?.id) {
            console.warn('[GoogleSDKProvider] Not ready or google.accounts.id missing');
            return;
        }
        window.google.accounts.id.prompt((notification: PromptMomentNotification) => {
            switch (notification.getMomentType()) {
                case 'skipped': // no token issued, user dismissed, etc.
                    console.log('[GoogleSDKProvider] One Tap skipped');
                    if (onNotDisplayed) {
                        console.log('[GoogleSDKProvider] onNotDisplayed callback provided, invoking it.');
                        onNotDisplayed();
                    }
                    break;
                case 'dismissed': // credential returned *or* user cancelled
                    console.log('[GoogleSDKProvider] One Tap dismissed - flow resolved');
                    /* no fallback – flow resolved */
                    break;
                default: /* 'display' is no longer delivered under FedCM */;
            }
        });
        console.log('[GoogleSDKProvider] Google ID prompt triggered');
    }, [ready]);

    const renderGoogleButton = useCallback((container: HTMLElement, cfg: any = {}) => {
        console.log('[GoogleSDKProvider] renderGoogleButton called', { ready, hasGoogle: !!window.google?.accounts?.id, container, cfg });
        if (!ready || !window.google?.accounts?.id || !container) {
            console.warn('[GoogleSDKProvider] Not ready, google.accounts.id missing, or container missing');
            return;
        }
        window.google.accounts.id.renderButton(container, {
            theme: 'outline',
            size: 'large',
            shape: 'pill',
            type: 'standard',
            ...cfg,
        });
        console.log('[GoogleSDKProvider] Google button rendered');
    }, [ready]);

    const signOut = useCallback(() => {
        console.log('[GoogleSDKProvider] signOut called');
        if (window.google?.accounts?.id?.disableAutoSelect) {
            window.google.accounts.id.disableAutoSelect();
            console.log('[GoogleSDKProvider] Google auto select disabled');
        }
        // Remove Google tokens from localStorage
        localStorage.removeItem("google_drive_token");
        console.log('[GoogleSDKProvider] Google Drive token removed from localStorage');
        // Optionally, clear cookies if needed (cannot clear HttpOnly cookies from JS)
    }, []);

    // Drive token logic (unchanged)
    const requestDriveToken = async (forceConsent: boolean = false): Promise<string> => {
        console.log('[GoogleSDKProvider] requestDriveToken called', { forceConsent });
        return new Promise((resolve, reject) => {
            const googleAny = window.google as any;
            if (!googleAny?.accounts?.oauth2) {
                console.error('[GoogleSDKProvider] Google Identity Services not ready');
                reject(new Error("Google Identity Services not ready"));
                return;
            }

            const tokenClient = googleAny.accounts.oauth2.initTokenClient({
                client_id: NEXT_PUBLIC_GOOGLE_CLIENT_ID,
                scope: SCOPES,
                include_granted_scopes: true,
                callback: (tokenResponse: any) => {
                    console.log('[GoogleSDKProvider] Token client callback', tokenResponse);
                    if (tokenResponse.error) {
                        console.error("[GoogleSDKProvider] Token error:", tokenResponse.error);
                        reject(new Error(tokenResponse.error));
                        return;
                    }

                    const expiresIn = tokenResponse.expires_in || 3600;
                    const expiryTime = Date.now() + expiresIn * 1000;
                    const tokenData = {
                        access_token: tokenResponse.access_token,
                        expiry: expiryTime,
                    };

                    setDriveToken(tokenResponse.access_token);
                    localStorage.setItem("google_drive_token", JSON.stringify(tokenData));
                    console.log('[GoogleSDKProvider] Drive token set and stored', tokenData);
                    resolve(tokenResponse.access_token);
                },
            });

            tokenClient.requestAccessToken({ prompt: forceConsent ? "consent" : "" });
            console.log('[GoogleSDKProvider] Token client requested access token', { forceConsent });
        });
    };



    // Add code client initialization after gsiReady is true
    useEffect(() => {
        if (!gsiReady) return;
        try {
            const client = (window as any).google.accounts.oauth2.initCodeClient({
                client_id: NEXT_PUBLIC_GOOGLE_CLIENT_ID,
                scope: SCOPES,
                include_granted_scopes: true,
                ux_mode: 'popup',
                callback: (resp: { code?: string; error?: string }) => {
                    if (resp.code && codeCallbackRef.current) {
                        console.log('[GoogleSDKProvider] Code received in callback', resp.code.substring(0, 10) + '...');
                        codeCallbackRef.current(resp.code);
                    } else if (resp.error) {
                        console.error('[GoogleSDKProvider] Code flow error', resp.error);
                    }
                }
            });
            setCodeClient(client);
            codeClientRef.current = client;
            console.log('[GoogleSDKProvider] Code client initialized');
        } catch (err) {
            console.error('[GoogleSDKProvider] Error initializing code client:', err);
        }
    }, [gsiReady]);

    const setCodeClientCallback = useCallback((cb: (code: string) => void) => {
        console.log('[GoogleSDKProvider] setCodeClientCallback called with new callback');
        codeCallbackRef.current = cb;

        // Return cleanup function
        return () => {
            console.log('[GoogleSDKProvider] Clearing code callback ref');
            codeCallbackRef.current = undefined;
        };
    }, []);

    const requestCode = useCallback(() => {
        console.log('[GoogleSDKProvider] requestCode called', { codeClientState: !!codeClient, codeClientRef: !!codeClientRef.current });

        // Try to use the ref first, as it might be more up-to-date
        const client = codeClientRef.current || codeClient;

        if (client) {
            try {
                client.requestCode();
                console.log('[GoogleSDKProvider] Code request initiated');
            } catch (err) {
                console.error('[GoogleSDKProvider] Error requesting code:', err);
                // If we have a callback and an error occurs, try to initialize a new OAuth flow directly
                if (codeCallbackRef.current && window.google?.accounts?.oauth2) {
                    console.log('[GoogleSDKProvider] Falling back to direct OAuth2 authorization');
                    tryDirectOAuth();
                }
            }
        } else {
            console.log('[GoogleSDKProvider] Code client not available yet, waiting and retrying...');
            // If the codeClient isn't ready yet, wait a bit and retry
            setTimeout(() => {
                console.log('[GoogleSDKProvider] Retrying requestCode...');
                const retryClient = codeClientRef.current || codeClient;
                if (retryClient) {
                    try {
                        retryClient.requestCode();
                        console.log('[GoogleSDKProvider] Code request initiated on retry');
                    } catch (e) {
                        console.error('[GoogleSDKProvider] Error on retry:', e);
                        // If we still fail, try direct OAuth as a last resort
                        if (codeCallbackRef.current && window.google?.accounts?.oauth2) {
                            tryDirectOAuth();
                        }
                    }
                } else if (codeCallbackRef.current && window.google?.accounts?.oauth2) {
                    // If we still don't have a client, try direct OAuth
                    tryDirectOAuth();
                }
            }, 500);
        }
    }, [codeClient]);

    // Fallback to direct OAuth flow as a last resort
    const tryDirectOAuth = useCallback(() => {
        if (!window.google?.accounts?.oauth2 || !codeCallbackRef.current) return;

        console.log('[GoogleSDKProvider] Attempting direct OAuth flow');
        try {
            const tempClient = window.google.accounts.oauth2.initCodeClient({
                client_id: NEXT_PUBLIC_GOOGLE_CLIENT_ID,
                scope: SCOPES,
                ux_mode: 'popup',
                callback: (resp: { code?: string; error?: string }) => {
                    if (resp.code && codeCallbackRef.current) {
                        console.log('[GoogleSDKProvider] Code received from direct OAuth', resp.code.substring(0, 10) + '...');
                        codeCallbackRef.current(resp.code);
                    } else if (resp.error) {
                        console.error('[GoogleSDKProvider] Direct OAuth error:', resp.error);
                    }
                }
            });

            tempClient.requestCode();
            console.log('[GoogleSDKProvider] Direct OAuth flow initiated');
        } catch (err) {
            console.error('[GoogleSDKProvider] Failed to initiate direct OAuth flow:', err);
        }
    }, []);

    // Add new method for direct authorization (bypassing one-tap)
    const requestDirectAuth = useCallback((customScopes?: string) => {
        console.log('[GoogleSDKProvider] requestDirectAuth called - bypassing one-tap');

        if (!gsiReady || !window.google?.accounts?.oauth2) {
            console.warn('[GoogleSDKProvider] Google OAuth2 not ready');
            toast({
                title: "Error",
                description: "Google Sign-In is not ready yet. Please try again in a moment.",
                variant: "destructive"
            });
            return;
        }

        try {
            // Create a direct OAuth client that bypasses one-tap
            const scopes = customScopes || SCOPES;
            const directClient = window.google.accounts.oauth2.initCodeClient({
                client_id: NEXT_PUBLIC_GOOGLE_CLIENT_ID,
                scope: scopes,
                include_granted_scopes: true,
                ux_mode: 'popup',
                callback: (resp: { code?: string; error?: string }) => {
                    if (resp.code && codeCallbackRef.current) {
                        console.log('[GoogleSDKProvider] Code received from direct auth', resp.code.substring(0, 10) + '...');
                        codeCallbackRef.current(resp.code);
                    } else if (resp.error) {
                        console.error('[GoogleSDKProvider] Direct auth error:', resp.error);
                        toast({
                            title: "Authentication Failed",
                            description: "Failed to authenticate with Google. Please try again.",
                            variant: "destructive"
                        });
                    }
                }
            });

            // Request the code directly without one-tap
            directClient.requestCode();
            console.log('[GoogleSDKProvider] Direct auth flow initiated');
        } catch (err) {
            console.error('[GoogleSDKProvider] Error in direct auth:', err);
            toast({
                title: "Error",
                description: "Failed to initialize Google authentication.",
                variant: "destructive"
            });
        }
    }, [gsiReady, toast]);

    // Provide the new minimal API and legacy fields for compatibility
    const contextValue: SDKState = {
        ready,
        gsiReady,
        pickerReady,
        driveToken,
        requestDriveToken,
        init,
        prompt,
        renderGoogleButton,
        signOut,
        setCodeClientCallback,
        requestCode,
        requestDirectAuth,
    };

    useEffect(() => {
        console.log('[GoogleSDKProvider] State changed', { ready, gsiReady, pickerReady, driveToken });
    }, [ready, gsiReady, pickerReady, driveToken]);

    return (
        <GoogleSDKContext.Provider value={contextValue}>
            {children}
        </GoogleSDKContext.Provider>
    );
};

export const useGoogleSDK = () => useContext(GoogleSDKContext); 